/**
 * Centralized sprint assignment utilities.
 * All sprint/story-point operations route through the unified updateIssue service.
 */

import { updateIssue } from '@/lib/issue-service'

/**
 * Assign an issue to a sprint with optional story points.
 *
 * @param issueId - The issue ID to assign
 * @param sprintId - The sprint ID to assign to (or null to remove from sprint)
 * @param storyPoints - Optional story points estimate
 * @returns Promise resolving to success status
 */
export async function assignIssueToSprint(
    issueId: number,
    sprintId: number | null,
    storyPoints?: number | null
): Promise<{ success: boolean; error?: string }> {
    const agile: { sprintId: number | null; storyPoints?: number | null } = {
        sprintId,
    }

    if (storyPoints !== undefined) {
        agile.storyPoints = storyPoints
    }

    return updateIssue(issueId, { agile })
}

/**
 * Update story points for an issue without changing sprint assignment.
 *
 * @param issueId - The issue ID
 * @param storyPoints - Story points estimate (or null to remove)
 * @returns Promise resolving to success status
 */
export async function updateStoryPoints(
    issueId: number,
    storyPoints: number | null
): Promise<{ success: boolean; error?: string }> {
    return updateIssue(issueId, { agile: { storyPoints } })
}

/**
 * Remove an issue from its current sprint.
 *
 * @param issueId - The issue ID to remove from sprint
 * @returns Promise resolving to success status
 */
export async function removeIssueFromSprint(
    issueId: number
): Promise<{ success: boolean; error?: string }> {
    return assignIssueToSprint(issueId, null)
}
